import { DateField, Input, List, ShowButton, Space, Table, Tabs } from '@pankod/refine-antd'
import { IResourceComponentsProps, useTranslate } from '@pankod/refine-core'
import { ExportToExcelButton } from 'components/ExportToExcelButton/ExportToExcelButton'
import { Order } from 'interfaces'
import { OrdersLiveList } from 'pages/ordersLive/ordersLiveList'
import { useEffect, useState } from 'react'
import { formatDateTime, getItemsOfProcessedOrdersExport, getOrders, sorter } from 'utility'
import { CANCELLED_STATUS, DEFAULT_DATE_TIME_FORMAT, DECREASE_RETURN_STATUS, INTERNAL_SPENDING_RETURN_STATUS, NO_STOCK_STATUS, PARCIAL_RETURN_STATUS, PENALIZATION_STATUS, PROCESSED_STATUS, RETURN_STATUS } from 'utility/constants'

// Deliberately separate from the general order list: Warehouse never loads the
// commercial columns which are not needed to receive and prepare an order.
export const WarehouseOrderList: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate()
  const [rows, setRows] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [tab, setTab] = useState(localStorage.getItem('lastWarehouseOrderListTab') || 'orders')

  const operationalSelect = 'id, estimated_costs_status, iva_status, iva_intra, alias, total, is_priority, completed, created_at, estimated_delivered_date, package_number, location, cancelation_date, initial_payment, return_date, locked, locked_by, status_id, sequra_disbursement_ref, status(id, name), marketplace_id(id, name), supplements(supplement, volumetric, description, status(id, name)), notes, process_status, processed_at, reviewed_at, estimated_notes, edited, invoices:invoice_id(id, reviewed)'

  const getConditionalClassName = (record: Order) => {
    if (record.cancelation_date && (record.status_id !== CANCELLED_STATUS && record.status_id !== PENALIZATION_STATUS && record.status_id !== NO_STOCK_STATUS)) return 'cancelled-order'
    if (record.return_date && (record.status_id !== RETURN_STATUS && record.status_id !== PARCIAL_RETURN_STATUS && record.status_id !== DECREASE_RETURN_STATUS && record.status_id !== INTERNAL_SPENDING_RETURN_STATUS)) return 'return-order'
    return ''
  }

  const exportItemsProcessedOrders = async () => {
    const { data, error } = await getItemsOfProcessedOrdersExport()
    if (error) return []

    return data?.map((item: any) => {
      const marketOrders = item.order_id?.market_orders?.filter((marketOrder: any) => !marketOrder.deleted) || []

      return {
        [t('item.fields.id')]: item.id || '',
        [t('item.fields.order_id')]: item.order_id?.id || '',
        [t('item.fields.alias')]: item.order_id?.alias || '',
        [t('item.fields.status_id')]: item.order_id?.status_id ? t(`order.status.${item.order_id.status_id}`) : '',
        [t('item.fields.estimated_delivered_date')]: item.order_id?.estimated_delivered_date ? formatDateTime(item.order_id.estimated_delivered_date, true) : '',
        [t('item.fields.purchase_id')]: marketOrders.map((marketOrder: any) => marketOrder.purchase_id || '').join(' | '),
        [t('market_order.fields.trackingId')]: marketOrders.map((marketOrder: any) => marketOrder.tracking_id || '').join(' | '),
        [t('item.fields.name')]: item.name || '',
        [t('item.fields.description')]: item.description || '',
        [t('item.fields.size')]: item.size?.toLowerCase() !== 'size error' ? item.size || '' : '',
        [t('item.fields.code')]: item.code?.toLowerCase() !== 'code error' ? item.code || '' : '',
        [t('item.fields.color')]: item.color?.toLowerCase() !== 'color error' ? item.color || '' : '',
        [t('item.fields.model')]: item.model?.toLowerCase() !== 'model error' ? item.model || '' : '',
        [t('item.fields.marketplace')]: item.marketplace_id?.id === 0 ? 'Pedido personalizado' : item.marketplace_id?.name || '',
        [t('item.fields.url')]: item.marketplace_id?.id === 0 ? item.url : `${item.marketplace_id?.original_url || ''}${item.url || ''}`,
        [t('item.fields.quantity')]: item.quantity || '',
        [t('item.fields.syniva_price')]: item.syniva_price || ''
      }
    }) || []
  }

  const load = async (selectedTab = tab) => {
    setLoading(true)
    const filters: any[] = selectedTab === 'despared'
      ? [
          { field: 'status_id', operator: 'eq', value: PROCESSED_STATUS },
          { field: 'location', operator: 'not', value: null }
        ]
      : [{ field: 'status_id', operator: 'gt', value: 0 }]
    if (search.trim()) filters.push({ field: 'id', operator: 'eq', value: Number(search) || -1 })
    const { data } = await getOrders(
      'id, alias, package_number, location, created_at, device, user(email), marketplace_id(id, name, original_url), items(custom_order_site)',
      1,
      100,
      filters,
      { field: 'id', ascending: false } as sorter
    )
    setRows((data || []).map((row: any) => ({
      ...row,
      purchase_url: row.marketplace_id?.id === 0
        ? row.items?.find((item: any) => item.custom_order_site)?.custom_order_site || ''
        : row.marketplace_id?.original_url || ''
    })))
    setLoading(false)
  }

  const handleTabChange = (key: string) => {
    setTab(key)
    localStorage.setItem('lastWarehouseOrderListTab', key)
    load(key)
  }

  useEffect(() => { load() }, [])

  const orderTable = <>
    <Space style={{ marginBottom: 16 }}>
      <Input
        allowClear
        placeholder="Buscar por ID de pedido"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onPressEnter={() => load()}
      />
    </Space>
    <Table loading={loading} dataSource={rows} rowKey="id" scroll={{ x: true }}>
      <Table.Column dataIndex="id" title={t('order.fields.id')} />
      <Table.Column dataIndex="alias" title={t('order.fields.alias')} />
      <Table.Column dataIndex="package_number" title={t('order.fields.package_number')} />
      <Table.Column dataIndex="location" title={t('order.fields.location')} />
      <Table.Column dataIndex="created_at" title={t('order.fields.createdAt')} render={(value) => <DateField value={value} format={DEFAULT_DATE_TIME_FORMAT} />} />
      <Table.Column dataIndex={['user', 'email']} title={t('order.fields.userEmail')} />
      <Table.Column dataIndex="device" title={t('order.fields.device')} />
      <Table.Column dataIndex={['marketplace_id', 'name']} title={t('order.fields.marketplace')} />
      <Table.Column dataIndex="purchase_url" title={t('order.fields.purchaseUrl')} render={(value) => value ? <a href={value} target="_blank" rel="noreferrer">{value}</a> : ''} />
      <Table.Column title={t('table.actions')} render={(_, record: any) => <ShowButton resourceNameOrRouteName="orders" recordItemId={record.id} hideText />} />
    </Table>
  </>

  return <Tabs activeKey={tab} onTabClick={handleTabChange}>
    <Tabs.TabPane tab={t('order.orders')} key="orders">
      <List
        title="Pedidos"
        breadcrumb={false}
        headerProps={{
          extra: <ExportToExcelButton
            exportButtonName="Items de pedidos procesados"
            filename="processed orders items"
            asyncFunction={exportItemsProcessedOrders}
          />
        }}
      >
        {orderTable}
      </List>
    </Tabs.TabPane>
    <Tabs.TabPane tab={t('order.desparedOrders')} key="despared">
      <OrdersLiveList
        select={operationalSelect}
        dataToExport={() => []}
        getConditionalClassName={getConditionalClassName}
        onlyPaidOrders={false}
        statusOrder={PROCESSED_STATUS}
        specialFilter="despared"
        warehouseView
      />
    </Tabs.TabPane>
  </Tabs>
}
